import React, { useEffect, useReducer, useState } from 'react';
import { func, string, bool } from 'prop-types';
import {
  Alert,
  Button,
  ButtonGroup,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  TextField
} from '@one-thd/sui-atomic-components';
import {
  customType,
  params,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStoreId } from '@thd-nucleus/experience-context';
import { SuccessDrawerBody } from './SuccessDrawerBody';

export const EmailListDrawerBody = ({
  listId,
  onClose,
  menuName,
  setMenuName,
  setEmailListDrawer,
  hideBackButton
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('EmailListDrawerBody.ready', {});
  }, []);

  const [emailStatus, setEmailStatus] = useState({ status: null, message: null });
  const [successDrawerBody, setSuccessDrawerBody] = useState(false);

  const initialValues = {
    email: '',
    emailError: false,
    emailValid: false,
    message: '',
    messageError: false,
    messageValid: false,
    senderName: '',
    senderNameError: null,
    senderNameValid: false,
    senderMessageError: null,
    format: 'HTML'
  };

  const storeId = useStoreId();

  const [share, shareResponse] = useDataModel('emailList', {
    lazy: true,
    skip: !listId,
    context: {
      withAuth: true
    },
    fetchPolicy: 'no-cache'
  });

  const [formValues, setFormValues] = useReducer(
    (currentFormValues, newFormValues) => ({ ...currentFormValues, ...newFormValues }), initialValues
  );

  const {
    senderName,
    senderNameError,
    senderNameValid,
    email,
    emailError,
    emailValid,
    message,
    messageError,
    senderMessageError,
    messageValid,
    format,
  } = formValues;

  const handleSenderNameChange = (event) => {
    const { id, value } = event.target;
    let error = null;
    const name = value.trim();
    if (name.length === 0) {
      error = 'You must enter a name.';
    }
    if (!name.match(/^[a-zA-Z0-9 ]*$/)) {
      error = 'Invalid Name: Only A-Z and 0-9 are allowed.';
    }
    setFormValues({ [id]: value, senderNameValid: !error, senderNameError: error });
  };

  const validateEmail = (value) => {
    // eslint-disable-next-line
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(value).toLowerCase());
  };

  const handleEmailChange = (event) => {
    const { id, value } = event.target;
    const isError = !validateEmail(value);
    setFormValues({ [id]: value, emailValid: !isError, emailError: isError });
  };

  const handleMessageChange = (event) => {
    const { id, value } = event.target;
    const truncated = value.slice(0, 300);
    let error = null;
    let isError = false;
    if (truncated.trim().length === 0) {
      isError = true;
      error = 'You must enter a message.';
      setFormValues({
        [id]: truncated,
        messageValid: !isError,
        messageError: isError,
        senderMessageError: error
      });
    } else if (!truncated.match(/^[a-zA-Z0-9 .,'!?$]*$/)) {
      isError = true;
      // eslint-disable-next-line
      error = `Messages can only contain letters (A-Z), numbers (0-9), and the following special characters ( . , ' ! ? $ )`;
      setFormValues({
        [id]: truncated,
        messageValid: !isError,
        messageError: isError,
        senderMessageError: error
      });
    } else {
      setFormValues({
        [id]: truncated,
        messageValid: true,
        messageError: false,
        senderMessageError: null
      });
    }
  };

  useEffect(() => {
    if (shareResponse?.data?.emailList?.id) {
      setEmailStatus({ status: 'success', message: 'Email List sent successfully' });
      setSuccessDrawerBody(true);
    }
  }, [shareResponse?.data?.emailList?.id]);

  useEffect(() => {
    if (shareResponse?.error) {
      setEmailStatus({
        status: 'error',
        message: 'There was an error while trying to send your email. Please verify the information and try again'
      });
    }
  }, [shareResponse?.error]);

  const handleFormSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    if (listId) {
      await share({
        variables: {
          email,
          format,
          from: senderName,
          id: listId,
          message,
          storeId
        }
      });
      LIFE_CYCLE_EVENT_BUS.trigger('product-list.email-list', {});
    } else {
      setEmailStatus({
        status: 'error',
        message: 'List ID is required to send the email list.'
      });
    }
  };

  const PreviousDrawer = () => {
    setEmailListDrawer(false);
    setMenuName('Share List');
  };

  if (successDrawerBody) {
    return (
      <SuccessDrawerBody
        onClose={onClose}
        menuName={menuName}
        setMenuName={setMenuName}
        setMainMenuDrawer={setEmailListDrawer}
        setEmailListDrawer={setEmailListDrawer}
        setSuccessDrawerBody={setSuccessDrawerBody}
      />
    );
  }

  const handleAlertClose = () => {
    setEmailStatus({ status: null, message: null });
  };

  const showAlert = emailStatus?.status === 'error';

  const renderAlert = () => {
    if (showAlert) {
      return (
        <Alert
          status={emailStatus.status}
          onClose={handleAlertClose}
          data-testid="email-list-alert"
        >
          {emailStatus.message}
        </Alert>
      );
    }
    return null;
  };

  return (
    <div className="sui-flex sui-flex-col sui-min-h-screen">
      <DrawerHeader title={menuName} onClose={onClose} onBack={hideBackButton ? null : PreviousDrawer} />
      <DrawerBody>
        {renderAlert()}
        <form
          className="sui-grid sui-gap-6"
          autoComplete="off"
          noValidate
          onSubmit={handleFormSubmit}
        >
          <TextField
            required
            id="senderName"
            label="Your Name"
            type="text"
            value={senderName}
            fullWidth
            status={senderNameError ? 'error' : null}
            statusMessage={senderNameError}
            onChange={handleSenderNameChange}
          />
          <TextField
            required
            id="email"
            label="Recipients Email"
            status={emailError ? 'error' : null}
            statusMessage={emailError ? 'Please enter a valid email.' : null}
            type="email"
            value={email}
            fullWidth
            onChange={handleEmailChange}
          />
          <TextField
            required
            id="message"
            label="Message"
            value={message}
            type="text"
            helpMessage="300 Characters Max"
            fullWidth
            status={messageError ? 'error' : null}
            statusMessage={senderMessageError}
            onChange={handleMessageChange}
            multiline
            minRows={4}
            maxRows={4}
          />
        </form>
      </DrawerBody>
      <DrawerFooter position="center">
        <ButtonGroup orientation="vertical">
          <Button
            variant="primary"
            fullWidth
            onClick={handleFormSubmit}
            disabled={!senderNameValid || !emailValid || !messageValid}
            type="submit"
            data-testid="send-email-list-button"
          >
            Send
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={onClose}
            data-testid="cancel-email-list-button"
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </div>
  );
};

EmailListDrawerBody.propTypes = {
  onClose: func.isRequired,
  listId: string.isRequired,
  menuName: string.isRequired,
  setMenuName: func,
  setEmailListDrawer: func,
  hideBackButton: bool
};

EmailListDrawerBody.defaultProps = {
  setMenuName: () => {},
  setEmailListDrawer: () => {},
  hideBackButton: false
};

EmailListDrawerBody.displayName = 'EmailListDrawerBody';

EmailListDrawerBody.dataModel = {
  emailList: params({
    id: stringType().isRequired(),
    email: stringType().isRequired(),
    from: stringType().isRequired(),
    message: stringType().isRequired(),
    format: customType('ListEmailFormat').enum(['HTML', 'PDF'], 'PDF'),
    storeId: stringType()
  }).mutation().shape({
    id: stringType()
  })
};
